import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Form,
  Input,
  InputNumber,
  Modal,
  Rate,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
} from 'antd';
import { ArrowLeftOutlined, CheckOutlined, DeleteOutlined, PlusOutlined, StopOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import CrmContactLink from '@/components/crm/CrmContactLink';
import { useAuthStore } from '@/store/authStore';
import { useUsers } from '@/hooks/useUsers';
import { useCrmActivities } from '@/hooks/useCrmActivities';
import {
  useCancelCrmDemo,
  useCompleteCrmDemo,
  useCreateCrmDemo,
  useCrmDemo,
  useUpdateCrmDemo,
} from '@/hooks/useCrmDemos';
import {
  CRM_DEMO_FIT_STATUSES,
  CRM_DEMO_GAP_RESOLUTIONS,
  CRM_DEMO_PRIORITIES,
  CRM_DEMO_REQUIREMENT_STATUSES,
  CRM_DEMO_TYPES,
  type CrmDemoFitStatus,
  type CrmDemoGapResolution,
  type CrmDemoInput,
  type CrmDemoPriority,
  type CrmDemoRequirement,
  type CrmDemoRequirementStatus,
} from '@/services/crmDemo.service';
import CrmRecordPicker, { type CrmRecordLink } from '@/components/crm/activities/CrmRecordPicker';
import CrmActivityTimeline, { crmPersonName } from '@/components/crm/activities/CrmActivityTimeline';
import EntityAttachmentsCard from '@/components/documents/EntityAttachmentsCard';
import ReportDocumentActions from '@/components/finance/report-document/ReportDocumentActions';
import { formatDate } from '@/utils/format/date';
import { DEMO_STATUS_COLOR } from './DemoRegisterPage';

const { Title, Text, Paragraph } = Typography;

type GridRow = CrmDemoRequirement & { key: string };

const FIT_COLOR: Record<string, string> = { Fit: 'green', Partial: 'gold', Gap: 'red' };

let keySeq = 0;
const newKey = () => `new-${++keySeq}`;

function linkFromParams(params: URLSearchParams): CrmRecordLink | null {
  const opp = params.get('opportunity_id');
  if (opp) return { kind: 'opportunity', id: opp };
  const lead = params.get('lead_id');
  if (lead) return { kind: 'lead', id: lead };
  const customer = params.get('customer_id');
  if (customer) return { kind: 'customer', id: customer };
  return null;
}

const DemoFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const isNew = !id;
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user } = useAuthStore();
  const perms = user?.permissions ?? [];
  const canEdit = Boolean(user?.is_super_admin || perms.includes(isNew ? 'crm.demo.create' : 'crm.demo.edit'));

  const { data: demo, isLoading } = useCrmDemo(id);
  const { data: users = [] } = useUsers({ is_active: true });
  const { data: activities } = useCrmActivities({ demo_id: id, include_system: true, limit: 200 }, !!id);
  const createMut = useCreateCrmDemo();
  const updateMut = useUpdateCrmDemo();
  const completeMut = useCompleteCrmDemo();
  const cancelMut = useCancelCrmDemo();

  const [form] = Form.useForm();
  const [link, setLink] = useState<CrmRecordLink | null>(() => linkFromParams(params));
  const [rows, setRows] = useState<GridRow[]>([]);
  const [completeOpen, setCompleteOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [completeForm] = Form.useForm();
  const [cancelForm] = Form.useForm();

  const readOnly = !canEdit || demo?.status === 'Cancelled';
  const userOptions = useMemo(() => users.map((u) => ({ value: u.id, label: crmPersonName(u) })), [users]);

  useEffect(() => {
    if (isNew) {
      form.setFieldsValue({ demo_type: 'Online', demo_date: dayjs().add(1, 'day').hour(10).minute(0), presenter_user_id: user?.id });
      return;
    }
    if (!demo) return;
    form.setFieldsValue({
      title: demo.title,
      demo_date: dayjs(demo.demo_date),
      duration_minutes: demo.duration_minutes,
      demo_type: demo.demo_type,
      presenter_user_id: demo.presenter_user_id,
      attendees: demo.attendees,
      modules_demonstrated: demo.modules_demonstrated,
    });
    setRows((demo.requirements ?? []).map((r) => ({ ...r, key: r.id ?? newKey() })));
  }, [demo, isNew, form, user?.id]);

  const updateRow = (key: string, patch: Partial<CrmDemoRequirement>) =>
    setRows((prev) =>
      prev.map((r) => {
        if (r.key !== key) return r;
        const next = { ...r, ...patch };
        if (patch.fit_status === 'Fit') next.gap_resolution = null;
        if (patch.fit_status && patch.fit_status !== 'Fit' && !next.gap_resolution) next.gap_resolution = 'Customization';
        return next;
      })
    );

  const addRow = () =>
    setRows((prev) => [...prev, { key: newKey(), requirement: '', priority: 'Medium', fit_status: 'Fit', status: 'Open' }]);

  const save = async () => {
    const v = await form.validateFields();
    if (isNew && !link) {
      Modal.warning({ title: 'Link the demo', content: 'Choose the opportunity, lead or customer this demo is for.' });
      return;
    }
    const missingDue = rows.find((r) => r.commitment?.trim() && !r.due_date);
    if (missingDue) {
      Modal.warning({ title: 'Commitment due date', content: 'Give every commitment a due date so it can be tracked.' });
      return;
    }
    const body: CrmDemoInput = {
      title: v.title ?? null,
      demo_date: v.demo_date.toISOString(),
      duration_minutes: v.duration_minutes ?? null,
      demo_type: v.demo_type,
      presenter_user_id: v.presenter_user_id ?? null,
      attendees: v.attendees ?? null,
      modules_demonstrated: v.modules_demonstrated ?? null,
      requirements: rows
        .filter((r) => r.requirement?.trim())
        .map(({ key: _key, committed_by_name: _n, follow_up_status: _s, follow_up_date: _d, ...r }) => r),
    };
    if (isNew && link) {
      body.opportunity_id = link.kind === 'opportunity' ? link.id : null;
      body.lead_id = link.kind === 'lead' ? link.id : null;
      body.customer_id = link.kind === 'customer' ? link.id : null;
      const created = await createMut.mutateAsync(body);
      navigate(`/growth/demos/${created.id}`, { replace: true });
    } else if (id) {
      await updateMut.mutateAsync({ id, body });
    }
  };

  const submitComplete = async () => {
    if (!id) return;
    const v = await completeForm.validateFields();
    await completeMut.mutateAsync({
      id,
      body: { overall_feedback: v.overall_feedback, customer_rating: v.customer_rating ?? null, next_steps: v.next_steps ?? null },
    });
    setCompleteOpen(false);
  };

  const submitCancel = async () => {
    if (!id) return;
    const v = await cancelForm.validateFields();
    await cancelMut.mutateAsync({ id, reason: v.reason });
    setCancelOpen(false);
  };

  const columns: ColumnsType<GridRow> = [
    { title: '#', key: 'no', width: 44, render: (_, __, i) => i + 1 },
    {
      title: 'Requirement',
      dataIndex: 'requirement',
      width: 260,
      render: (v: string, r) => (
        <Input.TextArea
          autoSize={{ minRows: 1, maxRows: 4 }}
          value={v}
          disabled={readOnly}
          onChange={(e) => updateRow(r.key, { requirement: e.target.value })}
        />
      ),
    },
    {
      title: 'Module',
      dataIndex: 'module',
      width: 130,
      render: (v: string | null, r) => (
        <Input value={v ?? ''} disabled={readOnly} onChange={(e) => updateRow(r.key, { module: e.target.value })} />
      ),
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      width: 100,
      render: (v: CrmDemoPriority, r) => (
        <Select<CrmDemoPriority>
          value={v}
          disabled={readOnly}
          style={{ width: '100%' }}
          onChange={(p) => updateRow(r.key, { priority: p })}
          options={CRM_DEMO_PRIORITIES.map((p) => ({ value: p, label: p }))}
        />
      ),
    },
    {
      title: 'Fit',
      dataIndex: 'fit_status',
      width: 105,
      render: (v: CrmDemoFitStatus, r) => (
        <Select<CrmDemoFitStatus>
          value={v}
          disabled={readOnly}
          style={{ width: '100%' }}
          onChange={(f) => updateRow(r.key, { fit_status: f })}
          options={CRM_DEMO_FIT_STATUSES.map((f) => ({ value: f, label: <Tag color={FIT_COLOR[f]}>{f}</Tag> }))}
        />
      ),
    },
    {
      title: 'Gap resolution',
      dataIndex: 'gap_resolution',
      width: 140,
      render: (v: CrmDemoGapResolution | null, r) =>
        r.fit_status === 'Fit' ? (
          <Text type="secondary">—</Text>
        ) : (
          <Select<CrmDemoGapResolution>
            value={v ?? undefined}
            disabled={readOnly}
            style={{ width: '100%' }}
            onChange={(g) => updateRow(r.key, { gap_resolution: g })}
            options={CRM_DEMO_GAP_RESOLUTIONS.map((g) => ({ value: g, label: g }))}
          />
        ),
    },
    {
      title: 'Effort',
      dataIndex: 'effort_estimate',
      width: 100,
      render: (v: string | null, r) => (
        <Input
          value={v ?? ''}
          placeholder="e.g. 3 days"
          disabled={readOnly}
          onChange={(e) => updateRow(r.key, { effort_estimate: e.target.value })}
        />
      ),
    },
    {
      title: 'Commitment to customer',
      dataIndex: 'commitment',
      width: 220,
      render: (v: string | null, r) => (
        <Input.TextArea
          autoSize={{ minRows: 1, maxRows: 3 }}
          value={v ?? ''}
          placeholder="What we promised (optional)"
          disabled={readOnly}
          onChange={(e) => updateRow(r.key, { commitment: e.target.value })}
        />
      ),
    },
    {
      title: 'Owner',
      dataIndex: 'committed_by',
      width: 160,
      render: (v: string | null, r) =>
        r.commitment?.trim() ? (
          <Select
            showSearch
            optionFilterProp="label"
            value={v ?? undefined}
            placeholder="Me"
            disabled={readOnly}
            style={{ width: '100%' }}
            onChange={(u) => updateRow(r.key, { committed_by: u })}
            options={userOptions}
          />
        ) : null,
    },
    {
      title: 'Due',
      dataIndex: 'due_date',
      width: 135,
      render: (v: string | null, r) =>
        r.commitment?.trim() ? (
          <DatePicker
            format="DD-MM-YYYY"
            value={v ? dayjs(v) : null}
            disabled={readOnly}
            onChange={(d) => updateRow(r.key, { due_date: d ? d.format('YYYY-MM-DD') : null })}
          />
        ) : null,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 150,
      render: (v: CrmDemoRequirementStatus, r) =>
        r.commitment?.trim() ? (
          <Space direction="vertical" size={2}>
            <Select<CrmDemoRequirementStatus>
              value={v}
              disabled={readOnly}
              style={{ width: '100%' }}
              onChange={(s) => updateRow(r.key, { status: s })}
              options={CRM_DEMO_REQUIREMENT_STATUSES.map((s) => ({ value: s, label: s }))}
            />
            {r.follow_up_status && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                Follow-up {r.follow_up_status.toLowerCase()}
                {r.follow_up_date ? ` · ${formatDate(r.follow_up_date)}` : ''}
              </Text>
            )}
          </Space>
        ) : null,
    },
    {
      title: '',
      key: 'del',
      width: 44,
      render: (_, r) =>
        readOnly ? null : (
          <Button
            type="text"
            danger
            size="small"
            icon={<DeleteOutlined />}
            onClick={() => setRows((prev) => prev.filter((x) => x.key !== r.key))}
          />
        ),
    },
  ];

  if (!isNew && isLoading) {
    return (
      <div style={{ padding: 24 }}>
        <Spin />
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }} wrap>
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/growth/demos')}>
            Demos
          </Button>
          <Title level={3} style={{ margin: 0 }}>
            {isNew ? 'New demo' : `Demo ${demo?.demo_number ?? ''}`}
          </Title>
          {demo && <Tag color={DEMO_STATUS_COLOR[demo.status]}>{demo.status}</Tag>}
        </Space>
        <Space wrap>
          {demo && (
            <ReportDocumentActions
              reportKey="crm-demo-requirements"
              params={{ id: demo.id }}
              documentId={demo.id}
              sendPermissions={['crm.demo.edit']}
            />
          )}
          {demo?.status === 'Planned' && canEdit && (
            <>
              <Button icon={<StopOutlined />} onClick={() => { cancelForm.resetFields(); setCancelOpen(true); }}>
                Cancel demo
              </Button>
              <Button
                icon={<CheckOutlined />}
                onClick={() => {
                  completeForm.setFieldsValue({
                    overall_feedback: demo.overall_feedback,
                    customer_rating: demo.customer_rating,
                    next_steps: demo.next_steps,
                  });
                  setCompleteOpen(true);
                }}
              >
                Mark done
              </Button>
            </>
          )}
          {!readOnly && (
            <Button type="primary" onClick={save} loading={createMut.isPending || updateMut.isPending}>
              {isNew ? 'Create demo' : 'Save'}
            </Button>
          )}
        </Space>
      </Space>

      {demo?.status === 'Done' && (
        <Alert
          type="success"
          showIcon
          style={{ marginBottom: 16 }}
          message={`Completed ${formatDate(demo.completed_at)} and logged as a Demo activity`}
          description={
            <Descriptions size="small" column={1}>
              <Descriptions.Item label="Feedback">{demo.overall_feedback}</Descriptions.Item>
              {demo.customer_rating ? (
                <Descriptions.Item label="Customer rating">
                  <Rate disabled value={demo.customer_rating} />
                </Descriptions.Item>
              ) : null}
              {demo.next_steps ? <Descriptions.Item label="Next steps">{demo.next_steps}</Descriptions.Item> : null}
            </Descriptions>
          }
        />
      )}

      <Card size="small" title="Demo details" style={{ marginBottom: 16 }}>
        <Form form={form} layout="vertical" disabled={readOnly}>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item label="For" required>
                {isNew ? (
                  <CrmRecordPicker value={link} onChange={setLink} kinds={['opportunity', 'lead', 'customer']} />
                ) : (
                  <Space wrap>
                    {demo?.opportunity && (
                      <Link to={`/growth/opportunities/${demo.opportunity.id}`}>
                        {demo.opportunity.opportunity_number} — {demo.opportunity.opportunity_name}
                      </Link>
                    )}
                    {!demo?.opportunity && demo?.lead && (
                      <Link to={`/growth/leads/${demo.lead.id}`}>{demo.lead.lead_number}</Link>
                    )}
                    {demo?.customer ? (
                      <Link to={`/growth/customers/${demo.customer.id}/360`}>{demo.customer.customer_name}</Link>
                    ) : (
                      <Text>{demo?.customer_name}</Text>
                    )}
                    {(demo?.customer?.phone || demo?.opportunity?.phone || demo?.lead?.phone) && (
                      <CrmContactLink
                        kind="phone"
                        value={demo?.customer?.phone || demo?.opportunity?.phone || demo?.lead?.phone}
                      />
                    )}
                    {(demo?.customer?.email || demo?.opportunity?.email || demo?.lead?.email) && (
                      <CrmContactLink
                        kind="email"
                        value={demo?.customer?.email || demo?.opportunity?.email || demo?.lead?.email}
                      />
                    )}
                  </Space>
                )}
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="title" label="Title">
                <Input maxLength={255} placeholder="e.g. Inventory & procurement walkthrough" />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item name="demo_date" label="Date & time" rules={[{ required: true }]}>
                <DatePicker showTime={{ format: 'HH:mm' }} format="DD-MM-YYYY HH:mm" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={12} md={4}>
              <Form.Item name="duration_minutes" label="Duration (min)">
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={12} md={4}>
              <Form.Item name="demo_type" label="Type" rules={[{ required: true }]}>
                <Select options={CRM_DEMO_TYPES.map((t) => ({ value: t, label: t === 'POC' ? 'Proof of concept' : t }))} />
              </Form.Item>
            </Col>
            <Col xs={24} md={10}>
              <Form.Item name="presenter_user_id" label="Presenter">
                <Select showSearch optionFilterProp="label" options={userOptions} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="attendees" label="Customer attendees (names and roles)">
                <Input.TextArea autoSize={{ minRows: 2, maxRows: 5 }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="modules_demonstrated" label="Modules demonstrated">
                <Input.TextArea autoSize={{ minRows: 2, maxRows: 5 }} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Card>

      <Card
        size="small"
        title="Requirements, fit / gap and commitments"
        style={{ marginBottom: 16 }}
        extra={
          !readOnly && (
            <Button size="small" icon={<PlusOutlined />} onClick={addRow}>
              Add requirement
            </Button>
          )
        }
      >
        <Paragraph type="secondary" style={{ marginTop: 0 }}>
          Commitments with a due date appear in the Follow-up Tracker for their owner.
        </Paragraph>
        <Table<GridRow>
          rowKey="key"
          size="small"
          columns={columns}
          dataSource={rows}
          pagination={false}
          scroll={{ x: 1500 }}
          locale={{ emptyText: 'No requirements recorded yet' }}
        />
      </Card>

      {!isNew && id && (
        <Row gutter={16}>
          <Col xs={24} lg={12}>
            <EntityAttachmentsCard entityType="CrmDemo" entityId={id} title="Attachments" readOnly={readOnly} />
          </Col>
          <Col xs={24} lg={12}>
            <Card size="small" title="Activity & follow-ups">
              <CrmActivityTimeline
                activities={activities?.items ?? []}
                hideSystemByDefault={false}
                emptyText="Nothing logged for this demo yet"
              />
            </Card>
          </Col>
        </Row>
      )}

      <Modal
        open={completeOpen}
        title="Mark demo as done"
        okText="Complete"
        onOk={submitComplete}
        onCancel={() => setCompleteOpen(false)}
        confirmLoading={completeMut.isPending}
        destroyOnClose
      >
        <Paragraph type="secondary">
          Save any changes to the requirements first. Completing logs a Demo activity on the linked records.
        </Paragraph>
        <Form form={completeForm} layout="vertical">
          <Form.Item name="overall_feedback" label="Customer feedback" rules={[{ required: true }]}>
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item name="customer_rating" label="How well did it land?">
            <Rate />
          </Form.Item>
          <Form.Item name="next_steps" label="Next steps">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={cancelOpen}
        title="Cancel demo"
        okText="Cancel demo"
        okButtonProps={{ danger: true }}
        onOk={submitCancel}
        onCancel={() => setCancelOpen(false)}
        confirmLoading={cancelMut.isPending}
        destroyOnClose
      >
        <Form form={cancelForm} layout="vertical">
          <Form.Item name="reason" label="Reason" rules={[{ required: true }]}>
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default DemoFormPage;
